import React, { useEffect, useState } from "react";
import { App as AntApp, Typography, Flex, Button, Card } from "antd";
import { PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { Trans, useTranslation } from "react-i18next";

import { BasicModal } from "../../../components/Shared";
import {
  ListAssistance,
  AssistanceForm,
} from "../../../components/Admin/AssistantComp";
import { EducaThemeProvider, InfoPanel, PageHeader } from "../../../ui";
import { BrandThemeProvider } from "../../../ui/theme/buildAntdTheme";
import { useThemeRuntime } from "../../../ui/theme/ThemeRuntimeProvider";
import { NEW_ASISTENTES } from "../../../ui/featureFlags";

import paso1 from "../../../assets/illustrations/guia-asistentes/paso1.png";
import paso2 from "../../../assets/illustrations/guia-asistentes/paso2.png";
import paso3 from "../../../assets/illustrations/guia-asistentes/paso3.png";

import styles from "./PageAssistant.module.scss";

const { Title, Paragraph, Text } = Typography;

export function Page_Assistant() {
  const { t } = useTranslation("assistants");
  const [showModal, setShowModal] = useState(false);
  const [reload, setReload] = useState(false);

  const onOpenCloseModal = () => setShowModal((prev) => !prev);
  const onReload = () => setReload((prev) => !prev);

  // Overrides del editor de estilos (guia rapida por pagina). En la sesion de un
  // diseñador ganan; para el resto, valor por defecto (i18n + asset).
  const { resolvedOverrides } = useThemeRuntime();
  const ov = (key, fallback) => {
    const v = resolvedOverrides[key];
    return typeof v === "string" && v.trim() ? v : fallback;
  };

  useEffect(() => {
    const imageKey = "imageAvatar";
    if (localStorage.getItem(imageKey)) localStorage.removeItem(imageKey);
  }, []);

  const steps = [
    {
      title: ov("text.assistant.guide.step1.title", t("page.step1Title")),
      description: ov("text.assistant.guide.step1.desc", t("page.step1Desc")),
      illustration: ov("image.assistant.guide.step1", paso1),
    },
    {
      title: ov("text.assistant.guide.step2.title", t("page.step2Title")),
      description: ov("text.assistant.guide.step2.desc", t("page.step2Desc")),
      illustration: ov("image.assistant.guide.step2", paso2),
    },
    {
      title: ov("text.assistant.guide.step3.title", t("page.step3Title")),
      description: ov("text.assistant.guide.step3.desc", t("page.step3Desc")),
      illustration: ov("image.assistant.guide.step3", paso3),
    },
  ];
  const guideTitleOv = ov("text.assistant.guide.title", undefined);

  const createModal = (
    <BasicModal
      show={showModal}
      close={onOpenCloseModal}
      title={t("page.createModalTitle")}
      size="large"
    >
      <AssistanceForm onClose={onOpenCloseModal} onReload={onReload} />
    </BasicModal>
  );

  // ── Modo nuevo (design system verde) ──────────────────────────────────────
  if (NEW_ASISTENTES) {
    const createButton = (
      <Button
        type="primary"
        size="large"
        icon={<PlusOutlined />}
        onClick={onOpenCloseModal}
      >
        {t("page.createButton")}
      </Button>
    );

    return (
      <BrandThemeProvider>
        <AntApp>
          <div className={styles.page}>
            <PageHeader
              title={t("page.title")}
              description={t("page.subtitle")}
              steps={steps}
              guideTitle={guideTitleOv}
              extra={createButton}
            />

            <Card
              className={styles.content}
              variant="outlined"
              styles={{ body: { padding: 16 } }}
            >
              <ListAssistance reload={reload} onReload={onReload} />
            </Card>

            {createModal}
          </div>
        </AntApp>
      </BrandThemeProvider>
    );
  }

  // ── Modo legacy (paleta azul) ─────────────────────────────────────────────
  return (
    <EducaThemeProvider>
      <AntApp>
        <div className={styles.page}>
          <Card
            className={styles.header}
            variant="outlined"
            styles={{ body: { padding: 20 } }}
          >
            <Flex vertical gap={10}>
              <Flex align="center" justify="space-between" gap={12} wrap>
                <Flex align="center" gap={10}>
                  <TeamOutlined className={styles.headerIcon} />
                  <Title level={2} className={styles.title}>
                    {t("page.title")}
                  </Title>
                </Flex>

                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={onOpenCloseModal}
                  className={styles.primaryAction}
                >
                  {t("page.createButton")}
                </Button>
              </Flex>

              <Paragraph className={styles.lead}>
                <Trans
                  i18nKey="page.introText"
                  t={t}
                  components={{ bold: <Text strong /> }}
                />
              </Paragraph>

              <InfoPanel items={steps} initiallyOpen={false} />
            </Flex>
          </Card>

          <Card
            className={styles.content}
            variant="outlined"
            styles={{ body: { padding: 16 } }}
          >
            <ListAssistance reload={reload} onReload={onReload} />
          </Card>

          {createModal}
        </div>
      </AntApp>
    </EducaThemeProvider>
  );
}
